import App from 'app/app.js';
import 'app/service/StatsdService.js';

/**
 * MLC-13290: fetches the identity and contents of the live session, so StorageService can decide
 * whether locally persisted data may be rehydrated.
 *
 * Why this exists: localStorage outlives the JSESSIONID cookie, and the index render always
 * issues a fresh session (even on a 304, even with a stale cookie) rather than restoring the old
 * one. A user returning within the client's 30-minute freshness window gets a new, empty session
 * while the client rehydrates fully populated job state from localStorage — nothing reconciles
 * the two, so cart re-pricing silently drops premium background upgrades.
 *
 * Deliberately does not read StorageService or interpret the payload: every StorageService
 * get/set stamps the idle clock (mlt.last.access) that the staleness check depends on, so
 * fetching here first and comparing in StorageService keeps that clock untouched until measured.
 */
function SessionSyncService($http, FlightRecorderService, StatsdService) {
  /**
   * Counts each failure so the fail-closed discard it causes is visible in aggregate, not only
   * per-incident in the flight recorder. Counted here (not at the reset) so first-time visitors,
   * who lose nothing, are included too — comparing against SESSION_SYNC_RESET_UNKNOWN shows the
   * actual customer impact.
   *
   * Never throws: this feeds the promise InitService.init() blocks the splash screen on. Also
   * under-reports by design — the network being down here is a common cause of landing here in
   * the first place, which is exactly when the counter call itself may fail.
   */
  const countFetchFailure = () => {
    try {
      StatsdService.incrementCounter('SESSION_SYNC_FETCH_FAILED').catch(angular.noop);
    } catch (e) {
      // Never rethrown, for the same reason as above.
      FlightRecorderService.log(`SessionSyncService:countFailed:${e?.message}`);
    }
  };

  /**
   * @return promise of {instanceId, jobNumbers, imageAuthorization}, or null if it could not be
   *         determined. null and an empty payload are different: null means "unknown", an empty
   *         payload means the session genuinely holds nothing.
   *
   * The index render always establishes a fresh session before this runs, so landing here means a
   * 500, a dead network, or a session that died mid-bootstrap (419) — all grounds to discard.
   */
  this.fetchServerSessionState = function () {
    return $http.get('session').then(
      function (response) {
        const serverSessionState = response?.data?.data || {};

        // The instanceId is logged, never the payload: it is the field a storage-loss report has
        // to line up against the client's stored one to explain a reset.
        FlightRecorderService.log(
          `SessionSyncService:fetched:instanceId=${serverSessionState.instanceId || 'none'}` +
            `:jobs=${(serverSessionState.jobNumbers || []).length}` +
            `:imageAuthorization=${!!serverSessionState.imageAuthorization}`
        );

        return serverSessionState;
      },
      function () {
        FlightRecorderService.log('SessionSyncService:fetchFailed');
        countFetchFailure();

        return null;
      }
    );
  };
}

App.service('SessionSyncService', ['$http', 'FlightRecorderService', 'StatsdService', SessionSyncService]);
